/**
 * Legacy courses URL handling.
 *
 * The courses module renamed its nested resources:
 *   - `categories` -> `modules`
 *   - `posts`      -> `lessons`
 *
 * The rest of the path is unchanged, so a legacy lesson URL maps to the current
 * one by swapping only those two segment labels. This holds for every context
 * the lesson page is mounted in:
 *
 *   standalone:   /courses/products/{productId}/categories/{moduleId}/posts/{lessonId}
 *              -> /courses/products/{productId}/modules/{moduleId}/lessons/{lessonId}
 *
 *   communities:  /communities/groups/{groupSlug}/learning/courses/products/{productId}/categories/{moduleId}/posts/{lessonId}
 *              -> /communities/groups/{groupSlug}/learning/courses/products/{productId}/modules/{moduleId}/lessons/{lessonId}
 *
 *   gokollab:     /{groupSlug}/learning/courses/{productId}/categories/{moduleId}/posts/{lessonId}   (note: no `/products/`)
 *              -> /{groupSlug}/learning/courses/{productId}/modules/{moduleId}/lessons/{lessonId}
 *
 * These helpers are intentionally pure (no Nuxt imports) so they can be unit tested
 * and reused by both the route-resolver and the access-guard middleware.
 */

/**
 * One regex per context that the legacy lesson URL can appear in. Each captures:
 *   1. the prefix up to (and including) the productId segment,
 *   2. the moduleId (legacy `categories` value),
 *   3. the lessonId (legacy `posts` value),
 *   4. an optional trailing slash (preserved on rewrite).
 *
 * Ordered most-specific first. The GoKollab shape is intentionally last because its
 * leading `/{groupSlug}` segment is broad; the required `/learning/courses/` +
 * `/categories/.../posts/...` structure keeps it from matching the other shapes.
 */
const LEGACY_COURSES_LESSON_PATH_REGEXES: RegExp[] = [
  // communities: /communities/groups/{groupSlug}/learning/courses/products/{productId}/categories/{moduleId}/posts/{lessonId}
  /^(\/communities\/groups\/[^/]+\/learning\/courses\/products\/[^/]+)\/categories\/([^/]+)\/posts\/([^/]+)(\/?)$/,
  // standalone: /courses/products/{productId}/categories/{moduleId}/posts/{lessonId}
  /^(\/courses\/products\/[^/]+)\/categories\/([^/]+)\/posts\/([^/]+)(\/?)$/,
  // gokollab: /{groupSlug}/learning/courses/{productId}/categories/{moduleId}/posts/{lessonId}
  /^(\/[^/]+\/learning\/courses\/[^/]+)\/categories\/([^/]+)\/posts\/([^/]+)(\/?)$/,
]

/**
 * Rewrites a legacy courses lesson path to its current `modules`/`lessons` shape,
 * preserving the surrounding context (standalone, communities, or gokollab) and any
 * trailing slash. Returns `null` when the path is not a legacy courses lesson URL.
 *
 * The prefix and id segments are copied verbatim (already URL-encoded in the path),
 * so the rewritten path is not re-encoded.
 */
export function getCanonicalCoursesLessonPath(path: string): string | null {
  for (const regex of LEGACY_COURSES_LESSON_PATH_REGEXES) {
    const match = regex.exec(path)
    if (match) {
      const [, prefix, moduleId, lessonId, trailingSlash] = match
      return `${prefix}/modules/${moduleId}/lessons/${lessonId}${trailingSlash}`
    }
  }

  return null
}

/**
 * Convenience predicate used by middleware that only needs to know whether a
 * path is a legacy courses lesson URL.
 */
export function isLegacyCoursesLessonPath(path: string): boolean {
  return getCanonicalCoursesLessonPath(path) !== null
}
